import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { tournaments } from '../../lib/api';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  ExternalLink,
  Filter,
  MapPin,
  Medal,
  Search,
  ShieldCheck,
  Sparkles,
  Trophy,
  Users,
} from 'lucide-react';
import { format } from 'date-fns';
import { ru, enGB, fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';


const LOCALES = { ru, en: enGB, fr };

const SURFACE_META = {
  hard: {
    labelKey: 'tournament.surfaces.hard',
    badge: 'bg-blue-100 text-blue-800 border-blue-200',
    dot: 'bg-blue-500',
    bar: 'from-blue-400 to-cyan-500',
  },
  clay: {
    labelKey: 'tournament.surfaces.clay',
    badge: 'bg-orange-100 text-orange-800 border-orange-200',
    dot: 'bg-orange-500',
    bar: 'from-orange-400 to-amber-500',
  },
  grass: {
    labelKey: 'tournament.surfaces.grass',
    badge: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    dot: 'bg-emerald-500',
    bar: 'from-emerald-400 to-lime-500',
  },
  carpet: {
    labelKey: 'tournament.surfaces.carpet',
    badge: 'bg-purple-100 text-purple-800 border-purple-200',
    dot: 'bg-purple-500',
    bar: 'from-purple-400 to-violet-600',
  },
  indoor: {
    labelKey: 'tournament.surfaces.indoor',
    badge: 'bg-zinc-100 text-zinc-700 border-zinc-200',
    dot: 'bg-zinc-500',
    bar: 'from-zinc-400 to-zinc-600',
  },
  other: {
    labelKey: 'tournament.surfaces.other',
    badge: 'bg-zinc-100 text-zinc-700 border-zinc-200',
    dot: 'bg-zinc-400',
    bar: 'from-zinc-300 to-zinc-500',
  },
};

const getSurfaceMeta = (surface) => SURFACE_META[(surface || '').toLowerCase()] || SURFACE_META.other;

const EmptyState = ({ icon: Icon, title, body }) => (
  <div className="rounded-3xl border border-dashed border-zinc-300 bg-white p-10 text-center shadow-sm">
    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-zinc-950 text-lime-300">
      <Icon size={30} />
    </div>
    <h2 className="mt-5 text-2xl font-semibold tracking-tight text-zinc-950">{title}</h2>
    <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-zinc-500">{body}</p>
  </div>
);

export const TournamentsPage = () => {
  const { t, i18n } = useTranslation();
  const locale = LOCALES[i18n.language] || enGB;

  const [tournamentsList, setTournamentsList] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({ country: '', city: '', category: '', surface: '' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const [tournamentsRes, registrationsRes] = await Promise.all([
        tournaments.getAll(filters),
        tournaments.getRegistrations(),
      ]);
      setTournamentsList(Array.isArray(tournamentsRes.data) ? tournamentsRes.data : []);
      setRegistrations(Array.isArray(registrationsRes.data) ? registrationsRes.data : []);
    } catch (error) {
      toast.error(t('tournament.loadError'));
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRegister = async (tournamentId) => {
    try {
      await tournaments.register(tournamentId);
      toast.success(t('tournament.registerSuccess'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('tournament.registerError'));
    }
  };

  const isRegistered = (tournamentId) => {
    return registrations.some((reg) => reg.tournament.id === tournamentId);
  };

  const filteredTournaments = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return tournamentsList;
    return tournamentsList.filter((tournament) =>
      [
        tournament.name,
        tournament.country,
        tournament.city,
        tournament.category,
        tournament.surface,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [searchTerm, tournamentsList]);

  const upcomingCount = useMemo(() => {
    const now = new Date();
    return tournamentsList.filter((tournament) => new Date(tournament.date) >= now).length;
  }, [tournamentsList]);

  const surfaceCounts = useMemo(() => {
    return tournamentsList.reduce((counts, tournament) => {
      const surface = (tournament.surface || 'other').toLowerCase();
      counts[surface] = (counts[surface] || 0) + 1;
      return counts;
    }, {});
  }, [tournamentsList]);

  const nextTournament = useMemo(() => {
    const now = new Date();
    return [...tournamentsList]
      .filter((tournament) => new Date(tournament.date) >= now)
      .sort((a, b) => new Date(a.date) - new Date(b.date))[0];
  }, [tournamentsList]);

  const summaryCards = [
    {
      label: t('tournament.available'),
      value: tournamentsList.length,
      detail: t('tournament.matchingFilters'),
      Icon: Trophy,
      color: 'text-zinc-900',
    },
    {
      label: t('tournament.status.upcoming'),
      value: upcomingCount,
      detail: t('tournament.futureDates'),
      Icon: CalendarDays,
      color: 'text-blue-700',
    },
    {
      label: t('tournament.applications'),
      value: registrations.length,
      detail: registrations.length ? t('tournament.submittedApplications') : t('tournament.noneYet'),
      Icon: CheckCircle2,
      color: registrations.length ? 'text-lime-700' : 'text-zinc-600',
    },
    {
      label: t('tournament.surfacesTitle'),
      value: Object.keys(surfaceCounts).length,
      detail: t('tournament.representedSurfaces'),
      Icon: Medal,
      color: 'text-purple-700',
    },
  ];

  const renderTournamentCard = (tournament) => {
    const registered = isRegistered(tournament.id);
    const surface = getSurfaceMeta(tournament.surface);
    const surfaceName = t(surface.labelKey);
    const tournamentDate = new Date(tournament.date);
    const isUpcoming = tournamentDate >= new Date();

    return (
      <article
        key={tournament.id}
        className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:border-lime-300 hover:shadow-xl hover:shadow-zinc-200/70"
        data-testid={`tournament-card-${tournament.id}`}
      >
        <div className={`h-1.5 bg-gradient-to-r ${registered ? 'from-lime-300 to-emerald-500' : surface.bar}`} />
        <div className="p-5">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="line-clamp-2 text-lg font-semibold tracking-tight text-zinc-950">{tournament.name}</h3>
              <p className="mt-2 flex items-center gap-2 text-sm text-zinc-500">
                <MapPin size={15} />
                {[tournament.city, tournament.country].filter(Boolean).join(', ')}
              </p>
            </div>
            <span
              className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold ${
                registered
                  ? 'border-lime-200 bg-lime-100 text-lime-800'
                  : isUpcoming
                    ? 'border-blue-200 bg-blue-100 text-blue-800'
                    : 'border-zinc-200 bg-zinc-100 text-zinc-700'
              }`}
            >
              {registered ? t('tournament.applied') : isUpcoming ? t('tournament.status.upcoming') : t('tournament.status.completed')}
            </span>
          </div>

          <div className="grid gap-3 rounded-xl border border-zinc-100 bg-zinc-50 p-4 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="text-zinc-500">{t('common.date')}</span>
              <span className="font-semibold text-zinc-950">{format(tournamentDate, 'dd MMM yyyy', { locale })}</span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-zinc-500">{t('tournament.category')}</span>
              <span className="rounded-full border border-zinc-200 bg-white px-3 py-1 text-xs font-semibold text-zinc-700">
                {tournament.category || '-'}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-zinc-500">{t('tournament.surface')}</span>
              <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${surface.badge}`}>
                {surfaceName}
              </span>
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-zinc-100 bg-white p-4">
            <div className="flex items-center gap-2 text-sm font-semibold text-zinc-950">
              <ShieldCheck className="text-lime-700" size={17} />
              {t('tournament.eligibility')}
            </div>
            <p className="mt-2 text-sm leading-6 text-zinc-500">
              {t('tournament.eligibilityBody')}
            </p>
          </div>

          <div className="mt-5 flex gap-2">
            {registered ? (
              <button
                type="button"
                className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-full border border-lime-200 bg-lime-50 px-4 text-sm font-semibold text-lime-800"
                disabled
                data-testid={`registered-${tournament.id}`}
              >
                <CheckCircle2 size={17} />
                {t('tournament.registered')}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => handleRegister(tournament.id)}
                className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-full bg-zinc-950 px-4 text-sm font-semibold text-white transition hover:bg-zinc-800"
                data-testid={`register-${tournament.id}`}
              >
                <Trophy size={17} />
                {t('tournament.apply')}
              </button>
            )}
            {tournament.external_url && (
              <a
                href={tournament.external_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 w-12 items-center justify-center rounded-full border border-zinc-200 text-zinc-700 transition hover:bg-zinc-50"
                data-testid={`external-link-${tournament.id}`}
                aria-label={t('tournament.openExternalAria')}
              >
                <ExternalLink size={18} />
              </a>
            )}
          </div>
        </div>
      </article>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f6f7f4] px-4 pb-24 pt-4 lg:px-8 lg:pb-10 lg:pt-8" data-testid="tournaments-page">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="rounded-3xl bg-zinc-950 p-8">
            <div className="h-5 w-36 animate-pulse rounded bg-white/15" />
            <div className="mt-6 h-12 w-2/3 animate-pulse rounded bg-white/15" />
            <div className="mt-4 h-4 w-1/2 animate-pulse rounded bg-white/10" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((item) => (
              <div key={item} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                <div className="h-4 w-24 animate-pulse rounded bg-zinc-200" />
                <div className="mt-5 h-8 w-16 animate-pulse rounded bg-zinc-100" />
              </div>
            ))}
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {[0, 1, 2, 3].map((item) => (
              <div key={item} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                <div className="h-1.5 rounded bg-zinc-100" />
                <div className="mt-5 h-5 w-40 animate-pulse rounded bg-zinc-200" />
                <div className="mt-4 h-24 animate-pulse rounded-xl bg-zinc-100" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="min-h-screen bg-[#f6f7f4] px-4 pb-24 pt-4 lg:px-8 lg:pb-10 lg:pt-8" data-testid="tournaments-page">
        <div className="mx-auto max-w-3xl rounded-3xl border border-rose-200 bg-rose-50 p-8 text-center shadow-sm">
          <AlertTriangle className="mx-auto text-rose-600" size={40} />
          <h1 className="mt-4 text-2xl font-semibold tracking-tight text-rose-950">{t('tournament.loadError')}</h1>
          <p className="mt-2 text-sm leading-6 text-rose-700">
            {t('tournament.loadErrorBody')}
          </p>
          <button
            type="button"
            onClick={fetchData}
            className="mt-6 inline-flex min-h-11 items-center justify-center rounded-full bg-rose-600 px-5 text-sm font-semibold text-white"
          >
            {t('tournament.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f7f4] px-4 pb-24 pt-4 lg:px-8 lg:pb-10 lg:pt-8" data-testid="tournaments-page">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="relative overflow-hidden rounded-3xl bg-zinc-950 p-6 text-white shadow-2xl shadow-zinc-300/60 md:p-8">
          <div className="pointer-events-none absolute inset-0 opacity-25">
            <div className="absolute -right-20 top-6 h-60 w-60 rounded-full border border-lime-300" />
            <div className="absolute bottom-8 left-8 h-px w-72 rotate-6 bg-lime-300" />
            <div className="absolute bottom-20 left-1/2 h-px w-80 -rotate-12 bg-white/40" />
          </div>
          <div className="relative grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-end">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-lime-200">
                <span className="h-2 w-2 rounded-full bg-lime-300" />
                {t('tournament.heroKicker')}
              </div>
              <h1 className="max-w-3xl text-4xl font-semibold tracking-tight md:text-5xl">{t('tournament.title')}</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-300 md:text-base">
                {t('tournament.heroBody')}
              </p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-medium text-zinc-300">{t('tournament.nextTournament')}</span>
                <CalendarDays className="text-lime-300" size={21} />
              </div>
              <div className="text-2xl font-semibold tracking-tight text-white">
                {nextTournament ? nextTournament.name : t('tournament.noUpcomingEvent')}
              </div>
              <p className="mt-2 text-sm text-zinc-300">
                {nextTournament ? format(new Date(nextTournament.date), 'dd MMM yyyy', { locale }) : t('tournament.adjustFilters')}
              </p>
            </div>
          </div>
        </section>

        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map(({ label, value, detail, Icon, color }) => (
            <div key={label} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm font-medium text-zinc-500">{label}</span>
                <Icon className={color} size={20} />
              </div>
              <div className={`text-3xl font-semibold tracking-tight ${color}`}>{value}</div>
              <p className="mt-1 truncate text-sm text-zinc-500">{detail}</p>
            </div>
          ))}
        </section>

        <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
          <div className="space-y-5">
            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-zinc-700">
                <Filter size={18} />
                {t('tournament.filters')}
              </div>
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_150px_150px_150px_150px_auto]">
                <label className="relative block md:col-span-2 xl:col-span-1">
                  <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                  <input
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={t('tournament.searchPlaceholder')}
                    className="min-h-11 w-full rounded-full border border-zinc-200 bg-zinc-50 py-3 pl-11 pr-4 text-sm text-zinc-900 outline-none transition placeholder:text-zinc-400 focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                  />
                </label>
                <input
                  type="text"
                  placeholder={t('tournament.country')}
                  className="min-h-11 rounded-full border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                  value={filters.country}
                  onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                  data-testid="filter-country"
                />
                <input
                  type="text"
                  placeholder={t('tournament.city')}
                  className="min-h-11 rounded-full border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                  value={filters.city}
                  onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                />
                <input
                  type="text"
                  placeholder={t('tournament.category')}
                  className="min-h-11 rounded-full border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                />
                <input
                  type="text"
                  placeholder={t('tournament.surface')}
                  className="min-h-11 rounded-full border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-900 outline-none transition focus:border-lime-300 focus:bg-white focus:ring-4 focus:ring-lime-100"
                  value={filters.surface}
                  onChange={(e) => setFilters({ ...filters, surface: e.target.value })}
                />
                <button
                  type="button"
                  onClick={fetchData}
                  className="inline-flex min-h-11 items-center justify-center rounded-full bg-zinc-950 px-5 text-sm font-semibold text-white transition hover:bg-zinc-800"
                  data-testid="apply-filters"
                >
                  {t('common.filter')}
                </button>
              </div>
            </div>

            {tournamentsList.length === 0 ? (
              <EmptyState
                icon={Trophy}
                title={t('tournament.emptyTitle')}
                body={t('tournament.emptyBody')}
              />
            ) : filteredTournaments.length === 0 ? (
              <EmptyState
                icon={Search}
                title={t('tournament.noMatchingTitle')}
                body={t('tournament.noMatchingBody')}
              />
            ) : (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {filteredTournaments.map(renderTournamentCard)}
              </div>
            )}
          </div>

          <aside className="space-y-5">
            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('tournament.myApplications')}</h2>
                  <p className="text-sm text-zinc-500">{t('tournament.applicationsBody')}</p>
                </div>
                <CheckCircle2 className="text-lime-700" size={22} />
              </div>
              {registrations.length > 0 ? (
                <div className="space-y-3">
                  {registrations.map((reg) => {
                    const surface = getSurfaceMeta(reg.tournament.surface);
                    const surfaceName = t(surface.labelKey);
                    return (
                      <div key={reg.registration.id} className="rounded-xl border border-lime-100 bg-lime-50 p-4">
                        <p className="font-semibold text-zinc-950">{reg.tournament.name}</p>
                        <p className="mt-1 text-sm text-lime-800">
                          {[reg.tournament.city, reg.tournament.country].filter(Boolean).join(', ')} - {format(new Date(reg.tournament.date), 'dd MMM yyyy', { locale })}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <span className="rounded-full border border-lime-200 bg-white px-3 py-1 text-xs font-semibold text-lime-800">
                            {t('tournament.applied')}
                          </span>
                          <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${surface.badge}`}>
                            {surfaceName}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-4 text-sm leading-6 text-zinc-500">
                  {t('tournament.noApplicationsBody')}
                </p>
              )}
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('tournament.surfaceMix')}</h2>
                  <p className="text-sm text-zinc-500">{t('tournament.surfaceMixBody')}</p>
                </div>
                <Sparkles className="text-blue-700" size={22} />
              </div>
              {Object.keys(surfaceCounts).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(surfaceCounts).map(([surfaceKey, count]) => {
                    const meta = getSurfaceMeta(surfaceKey);
                    return (
                      <div key={surfaceKey} className="flex items-center justify-between rounded-xl border border-zinc-100 bg-zinc-50 px-4 py-3">
                        <span className="flex items-center gap-2 text-sm font-semibold text-zinc-700">
                          <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} />
                          {t(meta.labelKey)}
                        </span>
                        <span className="text-sm font-semibold text-zinc-950">{count}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="rounded-xl border border-zinc-100 bg-zinc-50 p-4 text-sm leading-6 text-zinc-500">
                  {t('tournament.surfaceBreakdownEmpty')}
                </p>
              )}
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('tournament.contract')}</h2>
                  <p className="text-sm text-zinc-500">{t('tournament.safeFallbackFields')}</p>
                </div>
                <Users className="text-zinc-700" size={22} />
              </div>
              <p className="rounded-xl border border-zinc-100 bg-zinc-50 p-4 text-sm leading-6 text-zinc-500">
                {t('tournament.contractBody')}
              </p>
            </div>
          </aside>
        </section>
      </div>
    </div>
  );
};
